import type { Database } from '../../design/services';
import type { Config, Model, Project, User } from '../../design/types';
import type { IAM } from '../../design/services';
import { config as mksysConfig } from '../../firebase.config';
import { rootProject, bootstrapRoot } from './root';
import { bootstrapOrdering } from './ordering';
import { bootstrapBrampton } from './brampton';
import { bootstrapVancouver } from './vancouver';

export { rootProject };

export async function bootstrap(metaDatabase: Database, getDatabase: (projectId: string, type?: string, config?: Config) => Database, iam: IAM, skipLogin = false): Promise<void> {
  // Lookup stubs — only the fields list()/find() need to resolve paths and return entities.
  const M = (name: string, path: string) =>
    ({ name, path, type: 'collection' as const, properties: [] as string[], actions: [] } as unknown as Model);

  console.log('[bootstrap] checking existing models...');
  const existing = await metaDatabase.list(M('models', '/models'));
  console.log('[bootstrap] found', existing.length, 'models');
  if (existing.length > 0) {
    // Root & ordering already seeded — recover IDs and seed local DBs
    console.log('[bootstrap] recovering users...');
    const users = await metaDatabase.list(M('users', '/users'), [], {name: "Sammy"}) as User[];
    console.log('[bootstrap] found', users.length, 'users');
    console.log('[bootstrap] recovering projects...');
    const projects = await metaDatabase.list(M('projects', '/projects')) as Project[];
    console.log('[bootstrap] found', projects.length, 'projects:', projects.map(p => p.url));
    const orderingProject = projects.find(p => p.url === 'ordering');

    if (orderingProject) {
      console.log('[bootstrap] recovering ordering from', orderingProject.id, '...');
      const orderingMetaDb = getDatabase(orderingProject.id, 'firebase', mksysConfig as Config);
      console.log('[bootstrap] listing ordering models...');
      const orderingModels = await orderingMetaDb.list(M('models', '/models')) as Model[];
      console.log('[bootstrap] found', orderingModels.length, 'ordering models');
      const orderingProjects = await orderingMetaDb.list(M('projects', '/projects')) as Project[];
      const brampton = orderingProjects.find(p => p.url === 'brampton');
      const vancouver = orderingProjects.find(p => p.url === 'vancouver');

      const byPath = Object.fromEntries(orderingModels.map(m => [m.path, m])) as Record<string, Model>;
      const models = {
        orderingUserModel: byPath['/users'],
        categoriesModel: byPath['/categories'],
        catalogModel: byPath['/catalog'],
        locationsModel: byPath['/locations'],
        ordersModel: byPath['/orders'],
        orderPeriodsModel: byPath['/order-periods'],
        orderItemsModel: byPath['/order-items'],
      };

      if (brampton) bootstrapBrampton(getDatabase(brampton.id, 'local'), users[0].id, models);
      if (vancouver) bootstrapVancouver(getDatabase(vancouver.id, 'local'), users[0].id, models);
    }

    if (!skipLogin && users.length > 0) {
      const u = users[0];
      console.log('[bootstrap] logging in as', u.name);
      iam.login({ id: u.id, name: u.name as string, email: u.email as string, pic: u.pic as string });
    }
    console.log('[bootstrap] recovery complete');
    return;
  }

  // First run — seed everything
  console.log('[bootstrap] first run, seeding root...');
  const { users, models, orderingProjectId } = await bootstrapRoot(metaDatabase);
  console.log('[bootstrap] root done, seeding ordering...');
  const orderingMetaDb = getDatabase(orderingProjectId, 'firebase', mksysConfig as Config);
  const { models: orderingModels, bramptonProjectId, vancouverProjectId } = await bootstrapOrdering(orderingMetaDb, users, models, orderingProjectId);
  console.log('[bootstrap] ordering done, seeding brampton/vancouver...');
  bootstrapBrampton(getDatabase(bramptonProjectId, 'local'), users[0].id, orderingModels);
  bootstrapVancouver(getDatabase(vancouverProjectId, 'local'), users[0].id, orderingModels);
  if (!skipLogin) {
    const u = users[0];
    console.log('[bootstrap] done, logging in as', u.name);
    iam.login({ id: u.id, name: u.name as string, email: u.email as string, pic: u.pic as string });
  }
}
